import { NavLink } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import React from 'react'
import { GalleryVerticalEnd } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'

import { cn } from 'src/utils'
import { useLocales } from 'src/contexts'

import { useAppSelector } from 'src/hooks/useAppSelector'
import PasswordInput from 'src/components/PasswordInput'

import { SIGN_UP_FIELDS } from 'src/pages/SignUp/constants'
import extendedSchema from 'src/helpers/validations/signup'
import { useAuthLayout } from 'src/features/AuthLayout/useAuthLayout'
import { Input } from 'src/components/shadcn/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'src/components/shadcn/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/shadcn/card'
import { Button } from 'src/components/shadcn/button'

const SignUp = () => {
  const { translations } = useLocales()
  const { handleSignInWithGoogle, onSubmitSignupForm } = useAuthLayout()
  const { loading, error } = useAppSelector(state => state.auth)

  const signUpForm = useForm({
    resolver: zodResolver(extendedSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <a href="#" className="flex items-center gap-2 self-center font-medium">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <GalleryVerticalEnd className="size-4" />
          </div>
          {translations.brandName}
        </a>
        <div className={cn('flex flex-col gap-6')}>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">
                {translations.auth.SignUp}
              </CardTitle>
              <CardDescription>
                {translations.auth.loginGoogleAccount}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...signUpForm}>
                <form onSubmit={signUpForm.handleSubmit(onSubmitSignupForm)}>
                  <div className="grid gap-6">
                    <div className="flex flex-col gap-4">
                      <Button
                        onClick={handleSignInWithGoogle}
                        variant="outline"
                        className="w-full"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                            fill="currentColor"
                          />
                        </svg>
                        {translations.auth.signUpGoogle}
                      </Button>
                    </div>
                    <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                      <span className="relative z-10 bg-background px-2 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                    {error && (
                      <div className="mt-4 text-sm text-red-600 p-2 border-l-4 border-red-500 bg-red-100 rounded-md">
                        <p className="font-semibold">
                          {translations.fireBaseErrors[error]}
                        </p>
                      </div>
                    )}
                    {SIGN_UP_FIELDS.map(field => (
                      <FormField
                        key={field}
                        control={signUpForm.control}
                        name={field}
                        render={({ field: formField }) => (
                          <FormItem>
                            <FormLabel>
                              {translations.userData[`${field}`]}
                            </FormLabel>
                            <FormControl>
                              {field === 'password' ||
                              field === 'confirmPassword' ? (
                                <PasswordInput
                                  formField={formField}
                                  placeholder={
                                    translations.userData[
                                      `${field}_placeholder`
                                    ]
                                  }
                                />
                              ) : (
                                <Input
                                  type="text"
                                  placeholder={
                                    translations.userData[
                                      `${field}_placeholder`
                                    ]
                                  }
                                  {...formField}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}
                    <div className="mt-5 w-full justify-center flex">
                      <Button
                        className="w-full rounded-xl"
                        type="submit"
                        disabled={loading}
                      >
                        {translations.auth.SignUp}
                      </Button>
                    </div>
                    <div className="text-center text-sm">
                      {translations.auth.HaveAcc}{' '}
                      <NavLink
                        to="/login"
                        className="underline underline-offset-4"
                      >
                        {translations.auth.SignIn}
                      </NavLink>
                    </div>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
          <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary  ">
            {translations.auth.policyAgreement} <a href="#">Terms of Service</a>{' '}
            {translations.auth.and} <a href="#">Privacy Policy</a>.
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignUp
